import type { Visit, VisitMapPoint, VisitScope } from '@recipes/contracts';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  EmptyState,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@recipes/ui';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { CloudOff, MapPin, Plus, Star, Users, UtensilsCrossed } from 'lucide-react';
import { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Illustration } from '@/features/appearance/illustration';
import { toast } from '@/components/feedback/toaster';
import { useFeature } from '@/features/shared/hooks';
import { FeaturePageHeader, PageContainer } from '@/features/shared/page-header';
import {
  discardPendingVisit,
  startTastingSync,
  subscribeTastingSync,
  syncPendingVisits,
} from '@/features/tasting/capture-queue';
import { formatDateTime, formatDay } from '@/features/tasting/format';
import { usePendingVisits } from '@/features/tasting/hooks';
import { LazyTastingMap } from '@/features/tasting/map/lazy-map';
import { api } from '@/lib/api';
import { describeError } from '@/lib/errors';
import { useOnlineStatus } from '@/lib/hooks/use-online-status';
import { currentLocale } from '@/lib/i18n';
import { queryKeys } from '@/lib/query-keys';

interface Filters {
  scope: VisitScope;
  q: string;
  placeId: string;
  countryCode: string;
  city: string;
  cuisine: string;
  minRating: string;
  hasRecipe: string;
  from: string;
  to: string;
}

const NO_FILTERS: Filters = {
  scope: 'all',
  q: '',
  placeId: '',
  countryCode: '',
  city: '',
  cuisine: '',
  minRating: '',
  hasRecipe: '',
  from: '',
  to: '',
};
const ANY = '__any__';

function toQuery(filters: Filters): Record<string, string | number | boolean | undefined> {
  return {
    scope: filters.scope,
    q: filters.q.trim() || undefined,
    placeId: filters.placeId || undefined,
    countryCode: filters.countryCode || undefined,
    city: filters.city || undefined,
    cuisine: filters.cuisine || undefined,
    minRating: filters.minRating ? Number(filters.minRating) : undefined,
    hasRecipe: filters.hasRecipe ? filters.hasRecipe === 'yes' : undefined,
    from: filters.from ? new Date(`${filters.from}T00:00:00`).toISOString() : undefined,
    to: filters.to ? new Date(`${filters.to}T23:59:59`).toISOString() : undefined,
  };
}

export function VisitCard({ visit }: { visit: Visit }) {
  const { t } = useTranslation();
  const thumbs = visit.notes.flatMap((n) => n.photos.slice(0, 1)).slice(0, 4);
  const best = Math.max(0, ...visit.notes.map((n) => n.rating ?? 0));
  const dishes = visit.notes.map((n) => n.dishName).filter(Boolean);
  return (
    <Card className="transition-colors hover:bg-accent/40 focus-within:ring-2 focus-within:ring-ring">
      <Link
        to={`/tasting/visits/${visit.id}`}
        className="block rounded-lg outline-none"
        data-testid="visit-card"
      >
        <CardContent className="flex gap-3 p-3 md:p-4">
          <div className="grid size-20 shrink-0 grid-cols-2 gap-0.5 overflow-hidden rounded-md bg-muted">
            {thumbs.length ? (
              thumbs.map((photo, i) => (
                <img
                  key={photo.assetId}
                  src={photo.thumbnailUrl ?? photo.url ?? ''}
                  alt=""
                  loading="lazy"
                  className={`size-full object-cover ${thumbs.length === 1 ? 'col-span-2 row-span-2' : ''} ${thumbs.length === 3 && i === 0 ? 'row-span-2' : ''}`}
                />
              ))
            ) : (
              <span className="col-span-2 row-span-2 flex items-center justify-center">
                <UtensilsCrossed className="size-7 text-muted-foreground" aria-hidden />
              </span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="truncate font-semibold">
              {visit.place?.name ?? t('tasting.visit.unknownPlace')}
            </h3>
            <p className="truncate text-sm">
              {dishes.length
                ? dishes.join(', ')
                : t('tasting.visit.dishCount', { count: visit.notes.length })}
            </p>
            <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              <span>{formatDay(visit.visitedAt)}</span>
              {(visit.place?.city ?? visit.city) && (
                <span className="inline-flex items-center gap-1">
                  <MapPin className="size-3" aria-hidden />
                  {visit.place?.city ?? visit.city}
                </span>
              )}
              {best > 0 && (
                <span className="inline-flex items-center gap-1">
                  <Star className="size-3" aria-hidden />
                  <span className="sr-only">{t('tasting.dish.rating')}: </span>
                  {best}/5
                </span>
              )}
            </p>
            <p className="mt-1 flex flex-wrap gap-1">
              {!visit.place && visit.permissions.canManage && (
                <Badge variant="warning">{t('tasting.visit.confirmPlace')}</Badge>
              )}
              {visit.visibility === 'HOUSEHOLD' && (
                <Badge variant="info" className="gap-1">
                  <Users className="size-3" aria-hidden />
                  {visit.permissions.canManage
                    ? t('tasting.visit.shared')
                    : t('tasting.visit.sharedBy', { name: visit.ownerName })}
                </Badge>
              )}
              {visit.notes.some((n) => n.recipes.length > 0) && (
                <Badge variant="success">{t('tasting.visit.hasRecipe')}</Badge>
              )}
            </p>
          </div>
        </CardContent>
      </Link>
    </Card>
  );
}

function PendingVisits() {
  const { t } = useTranslation();
  const pending = usePendingVisits();
  const online = useOnlineStatus();
  const [syncing, setSyncing] = useState(false);
  if (!pending.length) return null;
  return (
    <section
      className="mb-4 space-y-2"
      aria-label={t('tasting.pending.title')}
      data-testid="pending-visits"
    >
      <h2 className="flex items-center gap-2 text-sm font-semibold">
        <CloudOff className="size-4" aria-hidden /> {t('tasting.pending.title')}
      </h2>
      <ul className="space-y-2">
        {pending.map((p) => (
          <li key={p.id} className="rounded-lg border border-dashed p-3 text-sm">
            <p className="font-medium">
              {p.notes.map((n) => n.dishName || t('tasting.pending.unnamed')).join(', ')}
            </p>
            <p className="text-xs text-muted-foreground">
              {formatDateTime(p.visitedAt)} · {t('tasting.pending.notSynced')}
              {p.lastError && ` · ${p.lastError}`}
            </p>
            {p.lastError && (
              <Button
                size="sm"
                variant="ghost"
                className="mt-1"
                onClick={() => void discardPendingVisit(p.id)}
              >
                {t('tasting.pending.discard')}
              </Button>
            )}
          </li>
        ))}
      </ul>
      <Button
        size="sm"
        variant="outline"
        disabled={!online}
        loading={syncing}
        onClick={() => {
          setSyncing(true);
          void syncPendingVisits().finally(() => setSyncing(false));
        }}
      >
        {t('tasting.pending.syncNow')}
      </Button>
    </section>
  );
}

/** Select with an "any" entry; hidden while there is nothing to choose from. */
function FilterSelect({
  label,
  value,
  onChange,
  options,
  testId,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  testId: string;
}) {
  const { t } = useTranslation();
  if (!options.length) return null;
  return (
    <div>
      <Label>{label}</Label>
      <Select value={value || ANY} onValueChange={(v) => onChange(v === ANY ? '' : v)}>
        <SelectTrigger className="mt-1" aria-label={label} data-testid={testId}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ANY}>{t('tasting.filters.any')}</SelectItem>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export function TastingVisitsPage() {
  const { t } = useTranslation();
  const enabled = useFeature('tasting_notes');
  const queryClient = useQueryClient();
  const ids = { q: useId(), from: useId(), to: useId() };
  const [view, setView] = useState<'list' | 'map'>('list');
  const [draft, setDraft] = useState<Filters>(NO_FILTERS);
  const [filters, setFilters] = useState<Filters>(NO_FILTERS);
  const [selected, setSelected] = useState<string[]>([]);
  const query = useMemo(() => toQuery(filters), [filters]);

  // Place, country, city and cuisine options come from the user's own places.
  const places = useQuery({
    queryKey: queryKeys.tasting.places({ limit: 100 }),
    queryFn: () => api.places.list({ limit: 100 }),
    enabled,
    staleTime: 60_000,
  });
  const options = useMemo(() => {
    const items = places.data?.items ?? [];
    const distinct = (values: Array<string | null>) =>
      [...new Set(values.filter((v): v is string => Boolean(v)))].sort((a, b) =>
        a.localeCompare(b),
      );
    return {
      places: items,
      countries: distinct(items.map((p) => p.countryCode)),
      cities: distinct(items.map((p) => p.city)),
      cuisines: distinct(items.flatMap((p) => p.cuisines)),
    };
  }, [places.data]);
  const regionNames = useMemo(() => {
    try {
      return new Intl.DisplayNames([currentLocale()], { type: 'region' });
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    startTastingSync();
    return subscribeTastingSync((result) => {
      if (!result.synced.length) return;
      toast.success(t('tasting.pending.synced', { count: result.synced.length }));
      void queryClient.invalidateQueries({ queryKey: queryKeys.tasting.visitsAll });
    });
  }, [queryClient, t]);

  const list = useInfiniteQuery({
    queryKey: queryKeys.tasting.visits(query),
    queryFn: ({ pageParam }) => api.visits.list({ ...query, cursor: pageParam, limit: 20 }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled,
  });
  const visits = useMemo(() => list.data?.pages.flatMap((p) => p.items) ?? [], [list.data]);

  // List and map share the same filters.
  const map = useQuery({
    queryKey: queryKeys.tasting.map(query),
    queryFn: () => api.visits.map(query),
    enabled: enabled && view === 'map',
  });
  const points = useMemo(() => map.data?.points ?? [], [map.data]);
  const selectedPoints = useMemo(
    () => points.filter((p) => selected.includes(p.visitId)),
    [points, selected],
  );

  if (!enabled)
    return (
      <PageContainer>
        <Alert variant="warning">{t('errors.FEATURE_DISABLED')}</Alert>
      </PageContainer>
    );

  return (
    <PageContainer>
      <FeaturePageHeader
        title={t('tasting.title')}
        description={t('tasting.description')}
        actions={
          <>
            <Button asChild variant="outline">
              <Link to="/tasting/places">{t('tasting.places.title')}</Link>
            </Button>
            <Button asChild>
              <Link to="/tasting/new" data-testid="tasting-new">
                <Plus aria-hidden /> {t('tasting.capture.title')}
              </Link>
            </Button>
          </>
        }
      />

      <PendingVisits />

      <form
        className="mb-4 grid gap-3 rounded-lg border p-3 sm:grid-cols-2 lg:grid-cols-6"
        role="search"
        aria-label={t('common.filter')}
        onSubmit={(e) => {
          e.preventDefault();
          setFilters(draft);
          setSelected([]);
        }}
      >
        <div className="lg:col-span-2">
          <Label htmlFor={ids.q}>{t('common.search')}</Label>
          <Input
            id={ids.q}
            className="mt-1"
            value={draft.q}
            placeholder={t('tasting.filters.searchPlaceholder')}
            onChange={(e) => setDraft({ ...draft, q: e.target.value })}
          />
        </div>
        <div>
          <Label>{t('tasting.filters.scope')}</Label>
          <Select
            value={draft.scope}
            onValueChange={(v) => setDraft({ ...draft, scope: v as VisitScope })}
          >
            <SelectTrigger className="mt-1" aria-label={t('tasting.filters.scope')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('tasting.filters.scopeAll')}</SelectItem>
              <SelectItem value="mine">{t('tasting.filters.scopeMine')}</SelectItem>
              <SelectItem value="household">{t('tasting.filters.scopeHousehold')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <FilterSelect
          label={t('tasting.filters.place')}
          value={draft.placeId}
          onChange={(placeId) => setDraft({ ...draft, placeId })}
          options={options.places.map((p) => ({ value: p.id, label: p.name }))}
          testId="filter-place"
        />
        <FilterSelect
          label={t('tasting.filters.country')}
          value={draft.countryCode}
          onChange={(countryCode) => setDraft({ ...draft, countryCode })}
          options={options.countries.map((code) => ({
            value: code,
            label: regionNames?.of(code) ?? code,
          }))}
          testId="filter-country"
        />
        <FilterSelect
          label={t('tasting.filters.city')}
          value={draft.city}
          onChange={(city) => setDraft({ ...draft, city })}
          options={options.cities.map((city) => ({ value: city, label: city }))}
          testId="filter-city"
        />
        <FilterSelect
          label={t('tasting.filters.cuisine')}
          value={draft.cuisine}
          onChange={(cuisine) => setDraft({ ...draft, cuisine })}
          options={options.cuisines.map((c) => ({ value: c, label: c }))}
          testId="filter-cuisine"
        />
        <div>
          <Label>{t('tasting.filters.minRating')}</Label>
          <Select
            value={draft.minRating || ANY}
            onValueChange={(v) => setDraft({ ...draft, minRating: v === ANY ? '' : v })}
          >
            <SelectTrigger className="mt-1" aria-label={t('tasting.filters.minRating')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>{t('tasting.filters.any')}</SelectItem>
              {[3, 4, 5].map((n) => (
                <SelectItem key={n} value={String(n)}>
                  {t('tasting.filters.stars', { count: n })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor={ids.from}>{t('tasting.filters.from')}</Label>
          <Input
            id={ids.from}
            type="date"
            className="mt-1"
            value={draft.from}
            onChange={(e) => setDraft({ ...draft, from: e.target.value })}
          />
        </div>
        <div>
          <Label htmlFor={ids.to}>{t('tasting.filters.to')}</Label>
          <Input
            id={ids.to}
            type="date"
            className="mt-1"
            value={draft.to}
            onChange={(e) => setDraft({ ...draft, to: e.target.value })}
          />
        </div>
        <div>
          <Label>{t('tasting.filters.hasRecipe')}</Label>
          <Select
            value={draft.hasRecipe || ANY}
            onValueChange={(v) => setDraft({ ...draft, hasRecipe: v === ANY ? '' : v })}
          >
            <SelectTrigger className="mt-1" aria-label={t('tasting.filters.hasRecipe')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>{t('tasting.filters.any')}</SelectItem>
              <SelectItem value="yes">{t('common.yes')}</SelectItem>
              <SelectItem value="no">{t('common.no')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end gap-2 sm:col-span-2 lg:col-span-6">
          <Button type="submit" variant="outline">
            {t('common.filter')}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setDraft(NO_FILTERS);
              setFilters(NO_FILTERS);
            }}
          >
            {t('tasting.filters.reset')}
          </Button>
        </div>
      </form>

      <Tabs value={view} onValueChange={(v) => setView(v as typeof view)}>
        <TabsList aria-label={t('tasting.viewLabel')}>
          <TabsTrigger value="list" data-testid="tasting-tab-list">
            {t('tasting.viewList')}
          </TabsTrigger>
          <TabsTrigger value="map" data-testid="tasting-tab-map">
            {t('tasting.viewMap')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="list">
          {list.isLoading ? (
            <Spinner label={t('app.loading')} />
          ) : list.error ? (
            <Alert variant="destructive">{describeError(list.error)}</Alert>
          ) : visits.length === 0 ? (
            <EmptyState
              illustration={<Illustration name="tasting" />}
              title={t('tasting.empty.title')}
              description={t('tasting.empty.description')}
              action={
                <Button asChild>
                  <Link to="/tasting/new">{t('tasting.capture.title')}</Link>
                </Button>
              }
            />
          ) : (
            <>
              <ul className="grid gap-3 md:grid-cols-2" data-testid="visit-list">
                {visits.map((visit) => (
                  <li key={visit.id}>
                    <VisitCard visit={visit} />
                  </li>
                ))}
              </ul>
              {list.hasNextPage && (
                <Button
                  variant="outline"
                  className="mt-4"
                  loading={list.isFetchingNextPage}
                  onClick={() => void list.fetchNextPage()}
                >
                  {t('common.loadMore')}
                </Button>
              )}
            </>
          )}
        </TabsContent>

        <TabsContent value="map" className="space-y-3">
          {map.error ? (
            <Alert variant="destructive">{describeError(map.error)}</Alert>
          ) : (
            <>
              {map.data?.truncated && <Alert variant="info">{t('tasting.map.truncated')}</Alert>}
              <LazyTastingMap
                ariaLabel={t('tasting.viewMap')}
                points={points.map((p) => ({
                  id: p.visitId,
                  lat: p.lat,
                  lng: p.lng,
                  label: p.placeName ?? p.dishNames.join(', '),
                  own: p.own,
                }))}
                fitToPoints
                onSelect={setSelected}
                className="h-[28rem] w-full overflow-hidden rounded-lg border"
              />
              <MapSelection points={selectedPoints} total={points.length} loading={map.isLoading} />
            </>
          )}
        </TabsContent>
      </Tabs>
    </PageContainer>
  );
}

function MapSelection({
  points,
  total,
  loading,
}: {
  points: VisitMapPoint[];
  total: number;
  loading: boolean;
}) {
  const { t } = useTranslation();
  if (loading) return <Spinner label={t('app.loading')} />;
  if (!points.length)
    return (
      <p className="text-sm text-muted-foreground" role="status">
        {total ? t('tasting.map.selectHint', { count: total }) : t('tasting.map.noPoints')}
      </p>
    );
  return (
    <ul className="grid gap-2 md:grid-cols-2" aria-live="polite" data-testid="map-selection">
      {points.map((p) => (
        <li key={p.visitId}>
          <Link
            to={`/tasting/visits/${p.visitId}`}
            className="flex gap-3 rounded-lg border p-3 hover:bg-accent/40"
          >
            {p.thumbnailUrl && (
              <img src={p.thumbnailUrl} alt="" className="size-14 rounded-md object-cover" />
            )}
            <span className="min-w-0">
              <span className="block truncate font-medium">
                {p.placeName ?? t('tasting.visit.unknownPlace')}
              </span>
              <span className="block truncate text-sm">{p.dishNames.join(', ')}</span>
              <span className="block text-xs text-muted-foreground">{formatDay(p.visitedAt)}</span>
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}
